/* eslint-disable react-hooks/rules-of-hooks */
import {
  CustomButton,
  CustomCheckbox,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMoneyInput,
  CustomNumberInput,
  errorDialog,
  Text,
  useText,
} from '@src/components';
import '@src/styles/cardOrder/cardDetailInformation.scss';
import '@src/styles/balanceUpload/cardListInformation.scss';
import { Collapse, Form } from 'antd';
import cardIcon from '@src/assets/icons/icon-kart.svg';
import { isGreaterMaximumLoadedAmount, moneyFormat } from '@src/utils/utils';
import iconCard from '@src/assets/icons/icon-card.svg';
import iconSearchWhite from '@src/assets/icons/icon-white-search.svg';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  getLoadBalanceDetailsGetPagedList,
  loadBalanceDetailPriceUpdate,
  setBalanceUploadDetails,
  setDetailTableFilter,
  setSelectedBalanceUploadDetails,
  resetBalanceFilterItems,
  setBalanceNotIncludedCards,
} from '@src/store/slice/balanceUploadDetailSlice';
import { addressGetPagedListByUserQuery } from '@src/store/slice/addressSlice';
import {
  getBalanceUploadById,
  setIsAllSelectedRowKeys,
  setRunTimeTotalPrice,
  setSelectedRowKeys,
} from '@src/store/slice/balanceUploadSlice';
import { setLoadingState } from '@src/store/slice/appSettingsSlice';
import { VirtualizedAndLazyScrollTable } from '@src/components/VirtualizedAndLazyScrollTable';
import { debounce } from 'lodash/function';
import CustomMultiSelect from '@src/components/CustomMultiSelect';
import CustomMoneyInputDaily from '@src/components/CustomMoneyInputDaily';
import CustomNumberInputDayCount from '@src/components/CustomNumberInputDayCount';
import {
  BALANCE_UPLOAD_ROW_KEY_PREFIX,
  buildClearedFilter,
  createSerialFetchQueue,
  isLoadBalanceSynced,
} from './balanceUploadHelpers';
const { Panel } = Collapse;

const CardListInformation = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const { addressListByUserQuery } = useSelector((state) => state?.address);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { maximumLoadedAmount } = useSelector((state) => state?.orderLimits);
  const { balanceUploadDetails, tableProperty, balanceNotIncludedCards } = useSelector(
    (state) => state?.balanceUploadDetail,
  );
  const { currentLoadBalance, selectedRowKeys, isAllSelectedRowKeys, currentMonthDayCount } =
    useSelector((state) => state?.balanceUpload);

  const [isBtnActive, setIsBtnActive] = useState(false);
  const [otherPagedPrice, setOtherPagedPrice] = useState(0);
  const [isGetAddresses, setIsGetAddresses] = useState(false);
  const [exceptSelectedCartCount, setExceptSelectedCart] = useState(0);
  const [selectedAddressListReset, setSelectedAddressListReset] = useState(false);
  const [disableMoneyInput, setDisableMoneyInput] = useState(0);

  const isExcelPage = !!(
    currentLoadBalance?.orderProcessType === 3 && currentLoadBalance?.totalCount > 0
  );

  const maximumLoadingLimitMessage = useText('maximumLoadingLimitPerCardIs');
  const amountPlaceholder = useText('amount3');
  const referenceNumberPlaceholder = useText('referenceNumber');
  const nameTitle = useText('nameUpperCase');
  const surnameTitle = useText('surnameUpperCase');
  const referenceCodeTitle = useText('referenceCode3');
  const cardNumberTitle = useText('cardNumber4');
  const deliveryAddressTitle = useText('deliveryAddressUpperCase');
  const totalBalanceTitle = useText('totalBalanceToBeLoadedVatIncludedUpperCase');
  const dailyAmountTitle = useText('dailyAmountUpperCase');
  const dayCountTitle = useText('dayCountUpperCase');

  const sortRef = useRef({
    orderType: '',
    orderByValue: '',
  });
  const store = useStore();
  const pageSizeRef = useRef(0);
  const syncedLoadBalanceRef = useRef({ id: undefined, totalPrice: undefined });
  const getStoreLoadBalance = () => store.getState().balanceUpload.currentLoadBalance;

  const fetchDetails = async (data, pageSize) => {
    const loadBalanceId = getStoreLoadBalance().id;
    const requestedPageSize = pageSize || pageSizeRef.current || 350;
    pageSizeRef.current = requestedPageSize;
    await dispatch(getBalanceUploadById(loadBalanceId));
    // Use the total accepted by the store; an outdated response is ignored by the reducer.
    const lastBalanceUpload = getStoreLoadBalance().totalPrice;
    syncedLoadBalanceRef.current = { id: loadBalanceId, totalPrice: lastBalanceUpload };
    const activePage = await dispatch(
      getLoadBalanceDetailsGetPagedList({
        body: { ...data, loadBalanceId },
        pageNumber: 1,
        pageSize: requestedPageSize,
        sortParams: sortRef.current,
      }),
    );
    const pageItems = activePage.payload?.data?.items || [];
    const lastCurrentBalanceUploadPaged = pageItems.reduce((acc, item) => acc + item.price, 0);
    setOtherPagedPrice(lastBalanceUpload - lastCurrentBalanceUploadPaged);
  };
  const fetchDetailsRef = useRef(fetchDetails);
  fetchDetailsRef.current = fetchDetails;

  const fetchQueueRef = useRef(null);
  if (!fetchQueueRef.current) {
    fetchQueueRef.current = createSerialFetchQueue(
      (data, pageSize) => fetchDetailsRef.current(data, pageSize),
      () => isLoadBalanceSynced(syncedLoadBalanceRef.current, getStoreLoadBalance()),
    );
  }
  const loadBalanceDetailsGetPagedList = fetchQueueRef.current;

  useEffect(() => {
    (async () => {
      if (addressListByUserQuery.length === 0 && !isGetAddresses) {
        setIsGetAddresses(true);
        await dispatch(
          addressGetPagedListByUserQuery({
            pageNumber: 0,
            pageSize: 0,
          }),
        );
      }
    })();
  }, [addressListByUserQuery, dispatch, isGetAddresses]);

  const totalPrice = useMemo(() => {
    const price =
      balanceUploadDetails?.reduce((acc, item) => {
        return acc + item?.price;
      }, 0) + otherPagedPrice;
    return Number.isNaN(price) ? 0 : price;
  }, [balanceUploadDetails, otherPagedPrice]);

  useEffect(() => {
    dispatch(setRunTimeTotalPrice(totalPrice));
  }, [totalPrice]);

  useEffect(() => {
    if (!currentLoadBalance?.id) return;
    loadBalanceDetailsGetPagedList({}, undefined, { onlyIfStale: true });
  }, [currentLoadBalance?.id, currentLoadBalance?.totalPrice]);

  useEffect(async () => {
    if (isAllSelectedRowKeys) {
      const selectedKeys = balanceUploadDetails
        .filter(
          (detail) =>
            !balanceNotIncludedCards.some((card) => card.cardNumber === detail.cardNumber),
        )
        .map((b) => `balance-upload-${b?.id}`);
      dispatch(setSelectedRowKeys(selectedKeys));
      if (balanceNotIncludedCards.length == 0) {
        setExceptSelectedCart(0);
      }
    }
  }, [balanceUploadDetails]);

  const addUpdatePrice = async (value, record, request = false) => {
    setDisableMoneyInput(record?.id);
    let val = value?.replaceAll('.', '')?.replace(',', '.');
    let v = val === '' ? 0 : parseFloat(val);
    if (record?.price !== v) {
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: val,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount),
        });
        return;
      }

      const body = {
        price: parseFloat(v),
        idList: [record?.id],
        LoadBalanceId: currentLoadBalance?.id,
        cardNumber: [record?.cardNumber],
        dontReFetch: true,
        customerId: selectedCustomer?.customerId,
      };
      // update BalanceUploadDetails with new price
      const newBalanceUploadDetails = balanceUploadDetails?.map((item) => {
        if (item?.id === record?.id) {
          return { ...item, price: body.price };
        }
        return item;
      });
      await dispatch(setBalanceUploadDetails(newBalanceUploadDetails));

      await dispatch(setLoadingState(false));
      request && (await dispatch(loadBalanceDetailPriceUpdate(body)));
      await dispatch(setLoadingState(true));
    }
    setDisableMoneyInput(0);
  };
  const addUpdatePriceDaily = async (value, record, request = false) => {
    let val = value?.replaceAll('.', '')?.replace(',', '.');

    const valIsNumeric = /^-?\d*(\.\d+)?$/.test(val);
    if (!valIsNumeric || val === '') {
      return;
    }

    let v = val === '' ? 0 : parseFloat(val * record?.dayCount);

    if (record?.price !== v || record?.dayCount == 0) {
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: val * record?.dayCount,
          dailyPrice: val,
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount),
        });

        const oldValue = record?.price
          ? (record?.price / record?.dayCount).toFixed(2).replace('.', ',')
          : '0';
        await dispatch(setLoadingState(false));
        await dispatch(
          setBalanceUploadDetails(
            balanceUploadDetails?.map((item) => {
              if (item?.id === record?.id) {
                return { ...item, dailyPrice: 0 };
              }
              return item;
            }),
          ),
        );
        await dispatch(
          setBalanceUploadDetails(
            balanceUploadDetails?.map((item) => {
              if (item?.id === record?.id) {
                return { ...item, dailyPrice: oldValue };
              }
              return item;
            }),
          ),
        );
        return;
      }

      const body = {
        price: record?.dayCount === 0 ? 0 : parseFloat(v),
        idList: [record?.id],
        dailyPrice: parseFloat(value?.replaceAll('.', '')?.replace(',', '.')),
        dayCount: record?.dayCount,
        LoadBalanceId: currentLoadBalance?.id,
        cardNumber: [record?.cardNumber],
        dontReFetch: true,
        customerId: selectedCustomer?.customerId,
      };

      let newBalanceUploadDetails = balanceUploadDetails?.map((item) => {
        if (item?.id === record?.id) {
          return {
            ...item,
            price: body.price,
            dailyPrice: body.dailyPrice,
            dayCount: body.dayCount,
          };
        }
        return item;
      });
      await dispatch(setBalanceUploadDetails(newBalanceUploadDetails));

      await dispatch(setLoadingState(false));
      request && (await dispatch(loadBalanceDetailPriceUpdate(body)));
      await dispatch(setLoadingState(true));
    }
  };

  const updateWithDebounceDaily = useCallback(
    debounce(async (value, record) => {
      await addUpdatePriceDaily(value, record, true);
    }, 650),
    [addUpdatePriceDaily],
  );

  const addUpdatePriceDayCount = async (value, record, request = false) => {
    let val = record?.dailyPrice;
    let v = val === '' ? 0 : parseFloat(val * Number(value));
    if (record?.price !== v || Number(value)) {
      if (
        isGreaterMaximumLoadedAmount({
          controlValue: maximumLoadedAmount,
          price: val * Number(value),
        })
      ) {
        errorDialog({
          title: <Text t="error" />,
          message: maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount),
        });
        await dispatch(
          setBalanceUploadDetails(
            balanceUploadDetails?.map((item) => {
              if (item?.id === record?.id) {
                return { ...item, dayCount: 0 };
              }
              return item;
            }),
          ),
        );

        await dispatch(
          setBalanceUploadDetails(
            balanceUploadDetails?.map((item) => {
              if (item?.id === record?.id) {
                return { ...item, dayCount: record?.dayCount ?? 0 };
              }
              return item;
            }),
          ),
        );
        return;
      }

      const data = {
        price: parseFloat(v),
        idList: [record?.id],
        dailyPrice: parseFloat(record?.dailyPrice),
        dayCount: Number(value),
        LoadBalanceId: currentLoadBalance?.id,
        cardNumber: [record?.cardNumber],
        dontReFetch: true,
        customerId: selectedCustomer?.customerId,
      };
      // update BalanceUploadDetails with new price
      let newBalanceUploadDetails = balanceUploadDetails?.map((item) => {
        if (item?.id === record?.id) {
          return {
            ...item,
            price: data.price,
            dailyPrice: data.dailyPrice,
            dayCount: data.dayCount,
          };
        }
        return item;
      });
      await dispatch(setBalanceUploadDetails(newBalanceUploadDetails));

      await dispatch(setLoadingState(false));
      request && (await dispatch(loadBalanceDetailPriceUpdate(data)));
      await dispatch(setLoadingState(true));
    }
  };

  const columnsMontly = [
    {
      title: nameTitle,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: surnameTitle,
      dataIndex: 'surname',
      key: 'surname',
      width: 100,
      sorter: (a, b) => (a.surname || '').localeCompare(b.surname || ''),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: referenceCodeTitle,
      dataIndex: 'refNo',
      key: 'refNo',
      width: 100,
      sorter: (a, b) => {
        const numA = parseFloat(a.refNo);
        const numB = parseFloat(b.refNo);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return (a.refNo || '').localeCompare(b.refNo || '');
      },
    },
    {
      title: cardNumberTitle,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 140,
      sorter: (a, b) => {
        const numA = parseFloat(a.cardNumber);
        const numB = parseFloat(b.cardNumber);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return 0;
      },
    },
    {
      title: deliveryAddressTitle,
      dataIndex: 'addressName',
      key: 'addressName',
      width: 100,
      sorter: (a, b) => (a.addressName || '').localeCompare(b.addressName || ''),
      sortDirections: ['descend', 'ascend'],
      ellipsis: true,
    },
    {
      title: totalBalanceTitle,
      dataIndex: 'price',
      key: 'price',
      width: 85,
      align: 'right',
      render: (text, record) => {
        return (
          <CustomMoneyInput
            id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-price-moneyInput"
            value={text}
            placeholder={amountPlaceholder}
            height={36}
            onBlur={(e) => addUpdatePrice(e.target.value, record, true)}
            addUpdatePrice={addUpdatePrice}
            record={record}
            disabled={record?.id === disableMoneyInput}
          />
        );
      },
    },
  ];

  const columnsDaily = [
    {
      title: nameTitle,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: surnameTitle,
      dataIndex: 'surname',
      key: 'surname',
      width: 100,
      sorter: (a, b) => (a.surname || '').localeCompare(b.surname || ''),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: referenceCodeTitle,
      dataIndex: 'refNo',
      key: 'refNo',
      width: 100,
      sorter: (a, b) => {
        const numA = parseFloat(a.refNo);
        const numB = parseFloat(b.refNo);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return (a.refNo || '').localeCompare(b.refNo || '');
      },
    },
    {
      title: cardNumberTitle,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 140,
      sorter: (a, b) => {
        const numA = parseFloat(a.cardNumber);
        const numB = parseFloat(b.cardNumber);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return 0;
      },
    },
    {
      title: deliveryAddressTitle,
      dataIndex: 'addressName',
      key: 'addressName',
      width: 100,
      sorter: (a, b) => (a.addressName || '').localeCompare(b.addressName || ''),
      sortDirections: ['descend', 'ascend'],
      ellipsis: true,
    },
    {
      title: dailyAmountTitle,
      dataIndex: 'dailyPrice',
      key: 'dailyPrice',
      width: 85,
      render: (text, record) => {
        return (
          <CustomMoneyInputDaily
            id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-price-moneyInput"
            value={text}
            height={36}
            record={record}
            placeholder={useText('dailyAmount')}
            required
            onInput={(e) => {
              updateWithDebounceDaily(e.target.value, record);
            }}
          />
        );
      },
    },
    {
      title: '',
      key: '',
      width: 20,
      align: 'left',
      render: (text, record) => {
        return <span style={{ color: 'rgb(0, 164, 131)' }}>X</span>;
      },
    },
    {
      title: dayCountTitle,
      key: 'dayCount',
      dataIndex: 'dayCount',
      width: 65,
      render: (text, record) => {
        return (
          <CustomNumberInputDayCount
            id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-dayCount"
            autoComplete={'off'}
            value={text == 0 ? '' : text}
            height={36}
            record={record}
            maxDays={currentMonthDayCount}
            onChange={(newValue) => addUpdatePriceDayCount(newValue, record, true)}
          />
        );
      },
    },

    {
      title: '',
      key: '',
      width: 20,
      align: 'left',
      render: () => {
        return <span style={{ color: 'rgb(0, 164, 131)' }}>=</span>;
      },
    },
    {
      title: totalBalanceTitle,
      dataIndex: 'price',
      key: 'price',
      width: 90,
      align: 'right',
      render: (text, record) => {
        return (
          <CustomMoneyInput
            id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-price-moneyInput"
            value={text}
            placeholder={amountPlaceholder}
            height={36}
            record={record}
            disabled={true}
          />
        );
      },
    },
  ];

  const clickFilter = async () => {
    dispatch(setSelectedRowKeys([]));
    dispatch(setSelectedBalanceUploadDetails([]));

    const values = await form.validateFields();
    let newValues;

    if (values.deliveryCode) {
      newValues = { ...values, addressIdList: [values.deliveryCode] };
    } else {
      newValues = { ...values, addressIdList: values.addressIdList };
    }

    await loadBalanceDetailsGetPagedList(
      values.cardNumber ? { ...newValues, cardNumber: values.cardNumber.toString() } : newValues,
      undefined,
      { replace: true },
    );
  };

  const clickClearForm = async () => {
    setIsBtnActive(false);
    form.resetFields();
    setSelectedAddressListReset(true);
    await dispatch(setDetailTableFilter({ body: { loadBalanceId: currentLoadBalance?.id } }));
    await loadBalanceDetailsGetPagedList(buildClearedFilter(form.getFieldsValue()), undefined, {
      replace: true,
    });
    await dispatch(resetBalanceFilterItems());
  };

  const onGridSelectChange = (record, data) => {
    setExceptSelectedCart(
      tableProperty?.totalCount < 20
        ? tableProperty?.totalCount - record.length
        : pageSizeRef.current - record.length,
    );
    dispatch(setSelectedRowKeys(record));
    dispatch(setSelectedBalanceUploadDetails(data));
  };

  const onValuesChange = async () => {
    const formValues = await form.getFieldsValue();
    if (
      (formValues?.cardNumber ||
        formValues?.addressIdList?.length ||
        formValues?.deliveryCode ||
        formValues?.name ||
        formValues?.referenceNumber ||
        formValues?.showOnlyPriced ||
        formValues?.surname) &&
      (formValues?.cardNumber !== '' ||
        formValues?.addressIdList?.length ||
        formValues?.deliveryCode !== '' ||
        formValues?.name !== '' ||
        formValues?.referenceNumber !== '' ||
        formValues?.showOnlyPriced ||
        formValues?.surname !== '')
    ) {
      setIsBtnActive(true);
      return;
    }
    setIsBtnActive(false);
  };

  const handleTableSortedChange = useCallback(
    async (pagination, filters, sorter, allTable) => {
      const { order, field } = sorter;
      if (!order) {
        sortRef.current = {};
      } else {
        sortRef.current = {
          OrderType: order === 'ascend' ? 'asc' : 'desc',
          OrderByValue: field,
        };
      }
      await loadBalanceDetailsGetPagedList({}, allTable?.currentDataSource.length || 30);
    },
    [loadBalanceDetailsGetPagedList],
  );

  const addressListForMultiSelect = useMemo(
    () =>
      addressListByUserQuery?.map((item) => {
        return {
          label: item.addressName,
          value: item.id,
        };
      }),
    [addressListByUserQuery],
  );

  const addressValueChange = async (values) => {
    form.setFieldsValue({ addressIdList: values.map((item) => ({ addressId: item.value })) });
    setSelectedAddressListReset(false);
    setTimeout(async () => {
      await onValuesChange();
    }, 200);
  };
  return (
    <CustomCollapseCard cardTitle={<Text t="userList" />} className="card-list-information">
      {!isExcelPage && (
        <div className="filter-container user-list-filter-container">
          <Collapse
            defaultActiveKey={['1']}
            className="filter-collapse-card"
            ghost
            expandIconPosition="right"
          >
            <Panel
              collapsible="disabled"
              header={
                <div>
                  <Text t="addFilter" />
                </div>
              }
              key="1"
              showArrow={false}
            >
              <div>
                <CustomForm
                  name="filterForm"
                  className="filter-form"
                  initialValues={{
                    showOnlyPriced: false,
                  }}
                  onValuesChange={onValuesChange}
                  autoComplete="off"
                  layout={'horizontal'}
                  form={form}
                  id="loadBalanceFilterForm"
                >
                  <div className="form-item">
                    <CustomFormItem name="name" className="name-form-item">
                      <CustomInput
                        id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-name-input"
                        placeholder={useText('name')}
                        height={36}
                      />
                    </CustomFormItem>
                    <CustomFormItem name="surname" className="surname-form-item">
                      <CustomInput
                        id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-surname-input"
                        placeholder={useText('surname')}
                        height={36}
                      />
                    </CustomFormItem>

                    <CustomFormItem name="cardNumber" className="card-number-form-item">
                      <CustomNumberInput
                        id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-cardNumber-numberInput"
                        autoComplete="off"
                        placeholder={useText('cardNumber2')}
                        height={36}
                      />
                    </CustomFormItem>

                    <CustomFormItem name="referenceNumber" className="reference-code-form-item">
                      <CustomInput
                        id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-refNo-input"
                        autoComplete="off"
                        placeholder={referenceNumberPlaceholder}
                        height={36}
                      />
                    </CustomFormItem>
                    <CustomFormItem
                      name="addressIdList"
                      style={{ display: 'none' }}
                    ></CustomFormItem>
                    <CustomMultiSelect
                      source={addressListForMultiSelect}
                      reset={selectedAddressListReset}
                      onCustomMultiSelectChange={addressValueChange}
                      selectSomeItems={useText('deliveryAddress2')}
                    />
                    <div className="form-footer">
                      <CustomFormItem
                        label={false}
                        name="showOnlyPriced"
                        className="footer-form-item"
                        valuePropName="checked"
                      >
                        <CustomCheckbox
                          id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-showOnlyPriced-checkbox"
                          className="only-amount-check"
                        >
                          <Text t="showThoseWithAmountEntered2" />
                        </CustomCheckbox>
                      </CustomFormItem>
                      <div className="action-buttons">
                        <CustomButton
                          id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-clear-btn"
                          className="clear-btn"
                          onClick={clickClearForm}
                        >
                          <Text t="clean" />
                        </CustomButton>
                        <CustomButton
                          id="ticketRestaurant-balanceUpload-balanceInformation-cardListInformation-search-btn"
                          disabled={!isBtnActive}
                          className="search-btn"
                          onClick={clickFilter}
                        >
                          <CustomImage className="icon-search" src={iconSearchWhite} />{' '}
                          <Text t="search" />
                        </CustomButton>
                      </div>
                    </div>
                  </div>
                </CustomForm>
              </div>
            </Panel>
          </Collapse>
        </div>
      )}
      {isExcelPage && currentLoadBalance?.totalCount > 0 && (
        <div className="header-info-container">
          <img src={iconCard} alt={useText('card')} />
          <span>
            {' '}
            <Text t="total" />
            <b className={'total-card-count'}>{currentLoadBalance?.totalCount}</b> <Text t="card" />
          </span>
        </div>
      )}

      {balanceUploadDetails.length > 0 && (
        <VirtualizedAndLazyScrollTable
          rowSelection={
            isExcelPage
              ? false
              : {
                preserveSelectedRowKeys: true,
                type: 'checkbox',
                selectedRowKeys,
                onChange: async (selectedRowKeys, selectedRows) => {
                  onGridSelectChange(selectedRowKeys, selectedRows);
                  const deselectedRows = balanceUploadDetails.filter(
                    (row) => !selectedRowKeys.includes(`balance-upload-${row?.id}`),
                  );
                  dispatch(setBalanceNotIncludedCards(deselectedRows));
                  if (deselectedRows?.length > 0) {
                    await dispatch(setIsAllSelectedRowKeys(false));
                  } else {
                    await dispatch(setIsAllSelectedRowKeys(true));
                  }
                },
                onSelectAll: async (selected) => {
                  await dispatch(setIsAllSelectedRowKeys(selected));
                },
              }
          }
          pagination={false}
          dataSource={balanceUploadDetails}
          getDataFunc={() => console.log('scroll')}
          columnsData={currentLoadBalance?.periodType == 0 ? columnsDaily : columnsMontly}
          rowKey={(record) => `${BALANCE_UPLOAD_ROW_KEY_PREFIX}${record?.id}`}
          onChange={handleTableSortedChange}
        />
      )}

      {createPortal(
        <div className="person-information-table-footer balance-list-information-table-footer">
          <div />
          <div className="footer-info-container">
            {!isExcelPage && (
              <div className="footer-info-content">
                <CustomImage src={cardIcon} />
                <Text t="total" /> <span>{selectedRowKeys.length}</span> <Text t="cardsSelected" />
              </div>
            )}
            <div className="total-amount-title">
              <Text t="totalBalanceToBeLoadedUpperCaseColon" />
              <span>{moneyFormat(totalPrice, true)}</span>
            </div>
          </div>
        </div>,
        document.getElementById('custom-sticky-container') || document.createElement('div'),
      )}
    </CustomCollapseCard>
  );
};

export default CardListInformation;
